"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Search, SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import HoverArrow from "@/components/shared/HoverArrow";
import {
  allLanguages,
  allSemesters,
  allStates,
  allSubjects,
  allTypes,
  countActiveFilters,
  type UniFilters,
} from "@/lib/universities";

type FiltersProps = {
  filters: UniFilters;
  searchDraft: string;
  onSearchChange: (value: string) => void;
  onChange: (filters: UniFilters) => void;
  onReset: () => void;
  resultCount: number;
};

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

function FilterGroup({
  label,
  defaultOpen = true,
  children,
}: {
  label: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-line py-4 first:border-t-0 first:pt-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left cursor-pointer group"
        aria-expanded={open}
      >
        <span className="text-sm font-bold text-ink group-hover:text-accent transition-colors">
          {label}
        </span>
        <ChevronDown
          size={16}
          className={cn(
            "text-ink-faint transition-transform duration-300",
            open && "rotate-180"
          )}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="pt-3 flex flex-col gap-1">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function CheckboxRow({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex items-center gap-3 px-2 py-1.5 -mx-2 rounded-lg cursor-pointer hover:bg-paper-deep transition-colors duration-150">
      <input type="checkbox" checked={checked} onChange={onToggle} className="sr-only" />
      <span
        className={cn(
          "w-[18px] h-[18px] shrink-0 rounded-md border flex items-center justify-center transition-all duration-150",
          checked ? "bg-accent border-accent text-paper" : "border-ink-faint/40 bg-white"
        )}
        aria-hidden="true"
      >
        {checked && <Check size={12} strokeWidth={3} />}
      </span>
      <span className={cn("text-sm", checked ? "text-ink font-medium" : "text-ink-soft")}>
        {label}
      </span>
    </label>
  );
}

function FilterBody({
  filters,
  searchDraft,
  onSearchChange,
  onChange,
  onReset,
}: Omit<FiltersProps, "resultCount">) {
  const activeCount = countActiveFilters(filters);

  return (
    <div className="flex flex-col">
      {/* Suche */}
      <div className="relative mb-5">
        <Search
          size={16}
          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none"
          aria-hidden="true"
        />
        <input
          type="search"
          value={searchDraft}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Uni oder Stadt suchen …"
          aria-label="Universität oder Stadt suchen"
          className="w-full rounded-full bg-paper-deep pl-10 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-accent/40 transition-shadow"
        />
      </div>

      <FilterGroup label="Semesterbeginn">
        {allSemesters.map((semester) => (
          <CheckboxRow
            key={semester}
            label={semester}
            checked={filters.semesters.includes(semester)}
            onToggle={() =>
              onChange({ ...filters, semesters: toggle(filters.semesters, semester) })
            }
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Studienfächer">
        {allSubjects.map((subject) => (
          <CheckboxRow
            key={subject}
            label={subject}
            checked={filters.subjects.includes(subject)}
            onToggle={() =>
              onChange({ ...filters, subjects: toggle(filters.subjects, subject) })
            }
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Einrichtung">
        {allTypes.map((type) => (
          <CheckboxRow
            key={type}
            label={type}
            checked={filters.types.includes(type)}
            onToggle={() => onChange({ ...filters, types: toggle(filters.types, type) })}
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Studiengebühren">
        {[
          { value: "free" as const, label: "Gebührenfrei" },
          { value: "paid" as const, label: "Kostenpflichtig" },
        ].map((option) => (
          <CheckboxRow
            key={option.value}
            label={option.label}
            checked={filters.tuition === option.value}
            onToggle={() =>
              onChange({
                ...filters,
                tuition: filters.tuition === option.value ? "" : option.value,
              })
            }
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Unterrichtssprache">
        {allLanguages.map((language) => (
          <CheckboxRow
            key={language}
            label={language}
            checked={filters.languages.includes(language)}
            onToggle={() =>
              onChange({ ...filters, languages: toggle(filters.languages, language) })
            }
          />
        ))}
      </FilterGroup>

      <FilterGroup label="Bundesland" defaultOpen={false}>
        {allStates.map((state) => (
          <CheckboxRow
            key={state}
            label={state}
            checked={filters.states.includes(state)}
            onToggle={() =>
              onChange({ ...filters, states: toggle(filters.states, state) })
            }
          />
        ))}
      </FilterGroup>

      {activeCount > 0 && (
        <button
          onClick={onReset}
          className="mt-4 inline-flex items-center justify-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-semibold text-ink-soft hover:text-ink hover:border-ink-faint transition-colors cursor-pointer"
        >
          <X size={14} aria-hidden="true" />
          Filter löschen ({activeCount})
        </button>
      )}
    </div>
  );
}

export default function SearchFilters(props: FiltersProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const activeCount = countActiveFilters(props.filters);

  return (
    <>
      {/* Desktop: Sticky-Sidebar */}
      <aside className="hidden lg:block lg:col-span-4 xl:col-span-3">
        <div className="card-flat sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto bg-white/60 p-6">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ink-faint mb-5">
            <SlidersHorizontal size={13} aria-hidden="true" />
            Ergebnisse filtern
          </p>
          <FilterBody {...props} />
        </div>
      </aside>

      {/* Mobile/Tablet: Sticky-Leiste + Slide-over */}
      <div className="lg:hidden sticky top-16 z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-paper/95 backdrop-blur-md border-b border-line flex items-center justify-between gap-3">
        <p className="text-sm text-ink-soft">
          <span className="font-bold text-ink tnum">{props.resultCount}</span>{" "}
          {props.resultCount === 1 ? "Universität" : "Universitäten"}
        </p>
        <button
          onClick={() => setMobileOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-accent text-paper px-4 py-2.5 text-sm font-semibold hover:bg-accent-deep transition-colors cursor-pointer min-h-[44px]"
        >
          <SlidersHorizontal size={15} aria-hidden="true" />
          Filter
          {activeCount > 0 && (
            <span className="tnum inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-paper text-accent text-xs font-bold">
              {activeCount}
            </span>
          )}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 z-[90] bg-ink/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          >
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-0 h-full w-full max-w-sm bg-paper shadow-2xl flex flex-col"
              role="dialog"
              aria-modal="true"
              aria-label="Filter"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-line">
                <p className="font-display font-bold text-lg text-ink">Filter</p>
                <button
                  onClick={() => setMobileOpen(false)}
                  aria-label="Filter schließen"
                  className="w-10 h-10 flex items-center justify-center rounded-lg text-ink-faint hover:bg-paper-deep hover:text-ink transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-5">
                <FilterBody {...props} />
              </div>
              <div className="px-5 py-4 border-t border-line bg-paper">
                <button
                  onClick={() => setMobileOpen(false)}
                  className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-accent text-paper px-6 py-3.5 text-sm font-semibold hover:bg-accent-deep transition-colors cursor-pointer min-h-[48px]"
                >
                  {props.resultCount} Ergebnisse anzeigen
                  <HoverArrow />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
